import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Loader2, Building2, CheckCircle2 } from 'lucide-react';

export default function AcceptAgencyInvite() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token');
  const [info, setInfo] = useState(null);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!token) { setError('Missing invitation token'); return; }
    try {
      const { data } = await api.get(`/agency/staff/invite-info?token=${encodeURIComponent(token)}`);
      setInfo(data);
      if (data.status !== 'pending') setError(`This invitation was already ${data.status}.`);
      else if (data.expired) setError('This invitation has expired.');
    } catch (e) { setError(e?.response?.data?.detail || 'Invitation not found'); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/agency/staff/invite-accept', { token, password: password || undefined });
      if (data.linked_existing) {
        toast.success('Access granted — please sign in');
        navigate('/login');
      } else {
        if (data.token) {
          // AuthContext only hydrates when BOTH token and user are stored.
          localStorage.setItem('token', data.token);
          localStorage.setItem('user', JSON.stringify({ id: data.user_id, user_id: data.user_id, email: data.email, role: data.role, client_approval_status: 'approved' }));
          localStorage.setItem('_last_login_at', String(Date.now()));
          ['impersonating', 'impersonated_user_email', 'original_token', 'original_user'].forEach(k => localStorage.removeItem(k));
        }
        toast.success('Welcome to the team!');
        window.location.href = '/agency/crm';
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not accept invitation'); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="accept-invite-page">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-cyan-100 flex items-center justify-center mb-2"><Building2 className="w-6 h-6 text-cyan-600" /></div>
          <CardTitle>Join {info?.agency_name || 'the agency'} on iEvntür</CardTitle>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="text-center">
              <p className="text-sm text-red-600 mb-4" data-testid="accept-error">{error}</p>
              <Button variant="outline" onClick={() => navigate('/login')}>Go to sign in</Button>
            </div>
          ) : !info ? (
            <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-cyan-600" /></div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">You're invited as <b>{info.role.replace('_', ' ')}</b> ({info.email}).</p>
              {info.account_exists ? (
                <div className="text-sm text-slate-600 bg-slate-50 rounded p-3 flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5" />
                  You already have an iEvntür account — we'll link it. Click below, then sign in as usual.
                </div>
              ) : (
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Create a password</label>
                  <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="At least 8 characters" data-testid="accept-password" />
                </div>
              )}
              <Button onClick={accept} disabled={busy || (!info.account_exists && password.length < 8)}
                className="w-full bg-cyan-600 hover:bg-cyan-700" data-testid="accept-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (info.account_exists ? 'Link my account' : 'Create account & join')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
